"use client";
import React from "react";
import {
    Accordion,
    AccordionItem,
    AccordionTrigger,
    AccordionContent,
} from '@/components/marketing/agent/faqs-accordion';
import { ChevronDown } from 'lucide-react';
import { useTranslations } from 'next-intl';

type Faq = {
    id: string;
    title: string;
    content: string;
};

const FAQ_ITEMS: Faq[] = [
    { id: 'what-is-theos', title: 'whatIsTheos.title', content: 'whatIsTheos.content' },
    { id: 'how-is-it-different', title: 'howDifferent.title', content: 'howDifferent.content' },
    { id: 'is-it-safe', title: 'isSafe.title', content: 'isSafe.content' },
    { id: 'who-is-it-for', title: 'whoFor.title', content: 'whoFor.content' },
    { id: 'what-results', title: 'whatResults.title', content: 'whatResults.content' },
    { id: 'how-to-start', title: 'howToStart.title', content: 'howToStart.content' },
];

function FaqItem({ id, title, content }: Faq) {
    const t = useTranslations('home.agent.faq.items');
    return (
        <AccordionItem value={id}>
            <AccordionTrigger className='group w-full text-left text-gray-950 dark:text-gray-50'>
                <div className='w-full flex items-center justify-between flex-1rounded py-4 text-sm font-medium transition-all gap-x-4 text-left tracking-tight text-foreground hover:text-foreground/80 hover:no-underline sm:py-4 md:py-[19px] lg:py-5 [&_svg]:size-7'>
                    <div className="text-18 leading-snug font-medium -tracking-[0.04em] text-pretty md:text-xl md:text-20 md:leading-[1.125] lg:text-24">{t(title)}</div>
                    <ChevronDown className='size-4 shrink-0 text-muted-foreground transition-transform duration-200 rotate-0 group-aria-[expanded=true]:rotate-180 group-data-[expanded]:rotate-180' />
                </div>
            </AccordionTrigger>
            <AccordionContent>
                <div className="pt-0 pb-4">
                    <div className="relative z-10 -mt-[4px] flex flex-col gap-y-5 pt-0 pr-8 pb-0 text-base !font-normal leading-snug tracking-tight text-gray-30 md:-mt-[4px] md:max-w-[616px] md:pt-0 md:pr-0 md:pb-0 md:text-18 lg:mt-0 lg:pb-[2px] lg:text-lg lg:text-20 xl:pb-[4px]">
                        <p>{t(content)}</p>
                    </div>
                </div>
            </AccordionContent>
        </AccordionItem>
    );
}

export default function Faqs() {
    const t = useTranslations('home.agent.faq');
    return (
        <section className="faq relative z-1 w-full py-12 md:py-16 lg:py-24 pt-[105px] pb-6 md:pt-[152px] md:pb-2.5 lg:pt-[198px] lg:pb-[122px] xl:pt-[218px] xl:pb-[85px]">
            <div className="mx-auto flex w-full max-w-7xl flex-col gap-y-5 px-5 md:px-8">
                <h2 className="text-28 leading-tight font-medium tracking-4 text-foreground md:text-4xl md:text-32 lg:mb-[16px] lg:text-40 xl:mb-[24px]">
                    {t('title')}
                </h2>
                <Accordion
                    className='flex w-full flex-col  divide-y divide-gray-200'
                    transition={{ duration: 0.2, ease: 'easeInOut' }}
                >
                    {FAQ_ITEMS.map((item) => (
                        <FaqItem key={item.id} {...item} />
                    ))}
                </Accordion>
            </div>
        </section>
    );
}